import { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Image as ImageIcon, Crop, User, Phone, Mail, MapPin, Globe, Loader2, ArrowRight } from 'lucide-react';
import { databaseService, storageService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import ProfileImageCropper from './ProfileImageCropper';

const CONTACT_SETTINGS_COLLECTION = 'contact_settings';
const CONTACT_BUCKET = 'contact_images';

export default function ContactSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsId, setSettingsId] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // profile, contact, social
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    profileImage: '',
    name: 'Parvej Hossain',
    title: 'GIS & Remote Sensing Specialist',
    description: 'I am available for impactful research that promotes sustainability and addresses global climate change and environment.',
    phone: '+880 1714 594091',
    email: 'official.parvej.hossain@gmail.com',
    location: 'Bangladesh',
    socialLinks: [
      { platform: 'LinkedIn', url: 'https://www.linkedin.com/in/parvej-iu/', icon: 'linkedin' },
      { platform: 'GitHub', url: 'https://github.com/iu-parvej/', icon: 'github' },
      { platform: 'Facebook', url: 'https://www.facebook.com/parvej.hossain.md/', icon: 'facebook' }
    ]
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(CONTACT_SETTINGS_COLLECTION);
      
      if (result.success && result.data.documents.length > 0) {
        const settings = result.data.documents[0];
        setSettingsId(settings.$id);
        
        let socialLinks = [];
        if (settings.socialLinks) {
          try {
            socialLinks = JSON.parse(settings.socialLinks);
          } catch (e) {
            socialLinks = [];
          }
        }
        
        setFormData({
          profileImage: settings.profileImage || '',
          name: settings.name || '',
          title: settings.title || '',
          description: settings.description || '',
          phone: settings.phone || '',
          email: settings.email || '',
          location: settings.location || '',
          socialLinks: socialLinks.length > 0 ? socialLinks : formData.socialLinks
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCropComplete = async (blob) => {
    setShowCropper(false);
    setUploadingImage(true);

    try {
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 8);
      const fileId = `profile_${timestamp}_${randomStr}.jpg`;

      const file = new File([blob], fileId, { type: 'image/jpeg' });

      const result = await storageService.uploadFile(CONTACT_BUCKET, fileId, file);
      if (result.success) {
        const fileUrl = storageService.getFileView(CONTACT_BUCKET, result.data.$id);
        setFormData(prev => ({ ...prev, profileImage: fileUrl }));
        showToast('Image uploaded', 'success');
      }
    } catch (error) {
      showToast('Failed to upload', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const dataToSave = {
        profileImage: formData.profileImage,
        name: formData.name,
        title: formData.title,
        description: formData.description,
        phone: formData.phone,
        email: formData.email,
        location: formData.location,
        socialLinks: JSON.stringify(formData.socialLinks.filter(link => link.platform && link.url))
      };

      let result;
      if (settingsId) {
        result = await databaseService.updateDocument(CONTACT_SETTINGS_COLLECTION, settingsId, dataToSave);
      } else {
        result = await databaseService.createDocument(CONTACT_SETTINGS_COLLECTION, dataToSave);
        if (result.success) setSettingsId(result.data.$id);
      }

      if (result.success) showToast('Profile updated successfully', 'success');
    } catch (error) {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Social Link Handlers
  const addSocialLink = () => setFormData(prev => ({ ...prev, socialLinks: [...prev.socialLinks, { platform: '', url: '', icon: 'link' }] }));
  const removeSocialLink = (idx) => setFormData(prev => ({ ...prev, socialLinks: prev.socialLinks.filter((_, i) => i !== idx) }));
  const updateSocialLink = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: prev.socialLinks.map((link, i) => i === idx ? { ...link, [field]: value } : link)
    }));
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#4C1D95] w-10 h-10" /></div>;

  return (
    <div className="max-w-7xl mx-auto space-y-8">
       {/* Header */}
       <div className="flex items-center justify-between">
          <div>
             <h1 className="text-3xl font-bold text-[#4C1D95]">Contact Settings</h1>
             <p className="text-gray-500 mt-1">Manage your public profile and contact information</p>
          </div>
          <button
             onClick={handleSubmit}
             disabled={saving}
             className="flex items-center gap-2 px-6 py-3 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] transition-all shadow-lg hover:shadow-xl font-bold active:scale-95 disabled:opacity-70"
          >
             {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
             {saving ? 'Saving Changes...' : 'Save Changes'}
          </button>
       </div>

       <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Form Sections */}
          <div className="lg:col-span-2 space-y-6">
             {/* Navigation Tabs */}
             <div className="flex p-1 bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
                {['profile', 'contact', 'social'].map((tab) => (
                   <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-bold capitalize transition-all ${
                         activeTab === tab 
                         ? 'bg-[#4C1D95] text-white shadow-md' 
                         : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                   >
                      {tab} Info
                   </button>
                ))}
             </div>

             {/* 1. Profile Section */}
             {activeTab === 'profile' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                       <User className="w-5 h-5 text-[#4C1D95]" /> Personal Details
                    </h2>
                    
                    <div className="flex flex-col md:flex-row gap-8 items-start">
                       {/* Image Upload */}
                       <div className="flex flex-col items-center gap-4">
                          <div className="relative group">
                             {formData.profileImage ? (
                                <img src={formData.profileImage} alt="Profile" className="w-32 h-32 rounded-2xl object-cover shadow-md ring-4 ring-gray-50" />
                             ) : (
                                <div className="w-32 h-32 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
                                   <ImageIcon className="w-10 h-10" />
                                </div>
                             )}
                             <button
                                type="button" 
                                onClick={() => setShowCropper(true)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center text-white font-bold text-sm cursor-pointer"
                             >
                                <Crop className="w-4 h-4 mr-1" /> Change
                             </button>
                          </div>
                          <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Profile Photo</span>
                       </div>

                       {/* Inputs */}
                       <div className="flex-1 w-full space-y-5">
                          <div>
                             <label className="block text-sm font-bold text-gray-700 mb-1.5">Full Name</label>
                             <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all font-medium" />
                          </div>
                          <div>
                             <label className="block text-sm font-bold text-gray-700 mb-1.5">Professional Title</label>
                             <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all" />
                          </div>
                          <div>
                             <label className="block text-sm font-bold text-gray-700 mb-1.5">Short Bio</label>
                             <textarea name="description" rows="4" value={formData.description} onChange={handleInputChange} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all resize-none" />
                          </div>
                       </div>
                    </div>
                </div>
             )}

             {/* 2. Contact Section */}
             {activeTab === 'contact' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                       <Mail className="w-5 h-5 text-[#4C1D95]" /> Contact Details
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                       <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">Email Address</label>
                          <div className="relative">
                             <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                             <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all" />
                          </div>
                       </div>
                       <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">Phone Number</label>
                          <div className="relative">
                             <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                             <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all" />
                          </div>
                       </div>
                       <div className="md:col-span-2">
                          <label className="block text-sm font-bold text-gray-700 mb-1.5">Location</label>
                          <div className="relative">
                             <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                             <input type="text" name="location" value={formData.location} onChange={handleInputChange} className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all" />
                          </div>
                       </div>
                    </div>
                </div>
             )}

             {/* 3. Social Section */}
             {activeTab === 'social' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="flex justify-between items-center mb-6">
                       <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                          <Globe className="w-5 h-5 text-[#4C1D95]" /> Social Profiles
                       </h2>
                       <button onClick={addSocialLink} className="text-sm font-bold text-[#4C1D95] hover:underline flex items-center gap-1">
                          <Plus className="w-4 h-4" /> Add Profile
                       </button>
                    </div>
                    
                    <div className="space-y-4">
                       {formData.socialLinks.map((link, idx) => (
                          <div key={idx} className="flex gap-4 items-start bg-gray-50 p-4 rounded-xl border border-gray-100 group">
                             <div className="flex-1 grid md:grid-cols-2 gap-4">
                                <div>
                                   <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Platform</label>
                                   <input type="text" value={link.platform} onChange={(e) => updateSocialLink(idx, 'platform', e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-[#4C1D95] outline-none" placeholder="e.g. LinkedIn" />
                                </div>
                                <div>
                                   <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">URL</label>
                                   <input type="text" value={link.url} onChange={(e) => updateSocialLink(idx, 'url', e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-[#4C1D95] outline-none" placeholder="https://..." />
                                </div>
                             </div>
                             <button onClick={() => removeSocialLink(idx)} className="mt-6 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                <Trash2 className="w-4 h-4" />
                             </button>
                          </div>
                       ))}
                       {formData.socialLinks.length === 0 && <p className="text-gray-400 italic text-center py-4">No social profiles added yet.</p>}
                    </div>
                </div>
             )}
          </div>

          {/* Right Column: Live Preview Card */}
          <div className="lg:col-span-1">
             <div className="sticky top-6 space-y-6">
                 <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Live Preview</h3>
                 
                 {/* Preview Card */}
                 <div className="bg-white rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 transform transition-all hover:scale-[1.02] duration-500">
                     {/* Cover Area */}
                     <div className="h-32 bg-[#4C1D95] relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#4C1D95] to-purple-900"></div>
                        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
                        <div className="absolute -left-10 bottom-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                     </div>
                     
                     {/* Content */}
                     <div className="px-6 pb-8 relative">
                        {/* Avatar */}
                        <div className="relative -mt-16 mb-4 inline-block">
                           <div className="p-1 bg-white rounded-2xl shadow-lg">
                              {formData.profileImage ? (
                                <img src={formData.profileImage} alt="Preview" className="w-28 h-28 rounded-xl object-cover" />
                              ) : (
                                <div className="w-28 h-28 rounded-xl bg-gray-100 flex items-center justify-center text-gray-300">
                                   <User className="w-10 h-10" />
                                </div>
                              )}
                           </div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 leading-tight mb-1">{formData.name || 'Your Name'}</h2>
                        <p className="text-[#4C1D95] font-medium text-sm mb-4">{formData.title || 'Your Title'}</p>
                        
                        <p className="text-gray-600 text-sm leading-relaxed mb-6">
                           {formData.description || 'Your description will appear here...'}
                        </p>

                        <div className="space-y-3 mb-8">
                           {formData.email && (
                              <div className="flex items-start gap-3 text-sm text-gray-600">
                                 <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-[#4C1D95] shrink-0">
                                    <Mail className="w-4 h-4" />
                                 </div>
                                 <span className="break-all">{formData.email}</span>
                              </div>
                           )}
                           {formData.phone && (
                              <div className="flex items-center gap-3 text-sm text-gray-600">
                                 <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-[#4C1D95]">
                                    <Phone className="w-4 h-4" />
                                 </div>
                                 <span>{formData.phone}</span>
                              </div>
                           )}
                           {formData.location && (
                              <div className="flex items-center gap-3 text-sm text-gray-600">
                                 <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-[#4C1D95]">
                                    <MapPin className="w-4 h-4" />
                                 </div>
                                 <span>{formData.location}</span>
                              </div>
                           )}
                        </div>
                        
                        {/* Social Icons Preview */}
                        <div className="flex gap-2">
                           {formData.socialLinks.map((link, i) => (
                              link.url && (
                                 <div key={i} className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-600 border border-gray-100 shadow-sm">
                                    <Globe className="w-4 h-4" />
                                 </div>
                              )
                           ))}
                        </div>
                     </div>
                 </div>
                 
                 <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3 text-sm text-blue-700">
                    <div className="mt-0.5"><div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div></div>
                    <p>This preview shows how your contact details will appear to visitors on the main site.</p>
                 </div>
             </div>
          </div>
       </div>

       {/* Image Cropper Modal */}
       {showCropper && (
         <ProfileImageCropper
           onCropComplete={handleCropComplete}
           onCancel={() => setShowCropper(false)}
         />
       )}
    </div>
  );
}
